import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Card,
  CardBody,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import apiClient from "../../axiosInstance";
import showAlert from "../../components/Common/ShowAlert";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TanstackTable from "../../components/Common/TanstackTable";

const actionOptions = ["create", "read", "update", "delete"];

const Permissions = () => {
  const [breadcrumbItems] = useState([
    { title: "Daryeel", link: "/" },
    { title: "Permissions", link: "#" },
  ]);

  const [data, setData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    feature: "",
    action: "read",
    description: "",
  });

  const fetchPermissions = async () => {
    try {
      const result = await apiClient.get("/permissions/getAllPermissions");
      setData(Array.isArray(result.data.response) ? result.data.response : []);
    } catch (err) {
      console.error("Error fetching permissions:", err);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, []);

  const groupedCount = useMemo(() => {
    const uniqueFeatures = new Set(data.map((item) => item.feature));
    return uniqueFeatures.size;
  }, [data]);

  const columns = useMemo(
    () => [
      {
        id: "index",
        header: "#",
        cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "code",
        header: "Code",
      },
      {
        accessorKey: "feature",
        header: "Feature",
      },
      {
        accessorKey: "action",
        header: "Action",
        cell: ({ getValue }) => (
          <span className="text-capitalize">{getValue()}</span>
        ),
      },
      {
        accessorKey: "description",
        header: "Description",
        cell: ({ getValue }) => getValue() || "-",
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => handleEditClick(row.original)}
            >
              Edit
            </button>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleDelete(row.original)}
            >
              Delete
            </button>
          </div>
        ),
      },
    ],
    [data],
  );

  const toggleModal = () => setModalOpen((prev) => !prev);

  const resetForm = () => {
    setForm({ feature: "", action: "read", description: "" });
    setEditId(null);
  };

  const handleCreateClick = () => {
    resetForm();
    setModalOpen(true);
  };

  const handleEditClick = (permission) => {
    setEditId(permission.id);
    setForm({
      feature: permission.feature || "",
      action: permission.action || "read",
      description: permission.description || "",
    });
    setModalOpen(true);
  };

  const handleDelete = async (permission) => {
    const confirmed = window.confirm(
      `Delete permission ${permission.code}? This will remove it from role mappings as well.`,
    );
    if (!confirmed) {
      return;
    }

    try {
      const response = await apiClient.delete(
        `/permissions/deletePermission/${permission.id}`,
      );
      await showAlert({
        title: "Permission",
        text: response.data.message,
        icon: response.data.status,
      });
      fetchPermissions();
    } catch (err) {
      console.error("Error deleting permission:", err);
      await showAlert({
        title: "Permission",
        text: err?.response?.data?.message || "Failed to delete permission",
        icon: "error",
      });
    }
  };

  const handleSave = async () => {
    if (!form.feature.trim()) {
      await showAlert({
        title: "Validation",
        text: "Feature is required",
        icon: "error",
      });
      return;
    }

    const payload = {
      feature: form.feature.trim().toLowerCase(),
      action: form.action,
      description: form.description.trim(),
    };

    try {
      let response;
      if (editId) {
        response = await apiClient.put(
          `/permissions/updatePermission/${editId}`,
          payload,
        );
      } else {
        response = await apiClient.post(
          "/permissions/createPermission",
          payload,
        );
      }

      await showAlert({
        title: "Permission",
        text: response.data.message,
        icon: response.data.status,
      });

      if (response.data.status === "success") {
        setModalOpen(false);
        resetForm();
        fetchPermissions();
      }
    } catch (err) {
      console.error("Error saving permission:", err);
      await showAlert({
        title: "Permission",
        text: err?.response?.data?.message || "Failed to save permission",
        icon: "error",
      });
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Permissions" breadcrumbItems={breadcrumbItems} />
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 className="mb-0">Permission List</h5>
                <small className="text-muted">
                  {data.length} permissions across {groupedCount} features
                </small>
              </div>
              <button className="btn btn-primary" onClick={handleCreateClick}>
                Create Permission
              </button>
            </div>

            <TanstackTable data={data} columns={columns} />
          </CardBody>
        </Card>

        <Modal isOpen={modalOpen} toggle={toggleModal} centered backdrop="static">
          <ModalHeader toggle={toggleModal}>
            {editId ? "Edit Permission" : "Create Permission"}
          </ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <label className="form-label">Feature</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g users"
                value={form.feature}
                onChange={(e) => setForm({ ...form, feature: e.target.value })}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Action</label>
              <select
                className="form-select"
                value={form.action}
                onChange={(e) => setForm({ ...form, action: e.target.value })}
              >
                {actionOptions.map((action) => (
                  <option key={action} value={action}>
                    {action}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                rows="3"
                placeholder="Optional description"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setModalOpen(false);
                resetForm();
              }}
            >
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave}>
              {editId ? "Update" : "Save"}
            </button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default Permissions;
